import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { Link } from '~/components/atoms/Link';
import { ForgotPasswordForm } from '~/components/organisms/ForgotPasswordForm';
import { LOGIN_PATH } from '~/constants';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import type { ForgotPasswordFormType } from '~/types/ForgotPassword';

const logger = new Logger('ForgotPassword');

export const ForgotPassword: FC = () => {
  const { warehouseApi } = useApi();
  const [showError, setShowError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const { mutate, isPending } = warehouseApi.forgotPassword.useMutation({
    onSuccess: () => {
      setShowSuccess(true);
    },
    onError: (error) => {
      logger.error('Error sending forgot password email', error);
      setShowError(true);
    },
  });

  const onSubmit = (data: ForgotPasswordFormType) => {
    mutate({ body: { data } });
  };

  return (
    <>
      <Stack spacing={4.75} maxWidth={360} alignSelf="center">
        <Typography component="h1" variant="subtitle1" fontWeight={600} textAlign="center">
          Forgot Password
        </Typography>

        {showSuccess ? (
          <Typography
            variant="subtitle2"
            fontWeight={600}
            textAlign="center"
            sx={({ palette }) => ({ color: palette.success.main })}
          >
            Please check your email to finish resetting your password.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} textAlign="center">
              Forgot your account&apos;s password? Enter your email address and we&apos;ll send you a recovery link.
            </Typography>

            <ForgotPasswordForm isPending={isPending} onSubmit={onSubmit} />
          </>
        )}

        <Typography
          id="forgot_password_back_to_login_link"
          component={Link}
          to={LOGIN_PATH}
          variant="body2"
          fontWeight={600}
          textAlign="center"
        >
          Back to Login
        </Typography>
      </Stack>

      <Snackbar open={showError} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" variant="standard" title="Unexpected error" onClose={() => setShowError(false)}>
          An unexpected error occurred. Please try again or contact support.
        </Alert>
      </Snackbar>
    </>
  );
};
